import React, { useEffect, useRef, useCallback, useState } from "react";
import {
    Excalidraw,
    getSceneVersion,
    viewportCoordsToSceneCoords,
    sceneCoordsToViewportCoords
} from "@excalidraw/excalidraw";
import { getDatabase, ref, get, onValue, update } from "firebase/database";
import { getAuth } from "firebase/auth";
import "@excalidraw/excalidraw/index.css";
import "./DiagramView.css";
import type { Member } from "../types";

interface DiagramViewProps {
    darkMode?: boolean;
    sessionId: string | null;
    isPrivate?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    socket?: any;
    members?: Member[];
    yourID?: string | null;
    /** Controlled: when provided, overrides internal state */
    isCollaborating?: boolean;
    onCollaborationChange?: (on: boolean) => void;
    /** System design interview instructions - shown in a panel above the canvas when present */
    systemDesignInstructions?: string | null;
}

const STORAGE_KEY_PREFIX = "excalidraw-";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExcalidrawAPI = any;

function loadFromStorage(sessionId: string | null): Record<string, unknown> | null {
    if (!sessionId) return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY_PREFIX + sessionId);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toDiagramData(elements: readonly any[], appState: any) {
    return {
        elements,
        appState: {
            viewBackgroundColor: appState.viewBackgroundColor,
            currentItemFontFamily: appState.currentItemFontFamily,
            currentItemStrokeColor: appState.currentItemStrokeColor,
            currentItemBackgroundColor: appState.currentItemBackgroundColor,
        },
    };
}

/** Firebase rejects undefined - strip it recursively from objects before writing */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
    if (obj === undefined) return null;
    if (obj === null) return null;
    if (Array.isArray(obj)) return obj.map(stripUndefined);
    if (typeof obj === "object") {
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj)) {
            if (v !== undefined) {
                result[k] = stripUndefined(v);
            }
        }
        return result;
    }
    return obj;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function saveToStorage(sessionId: string | null, elements: readonly any[], appState: any) {
    if (!sessionId) return;
    try {
        const toSave = toDiagramData(elements, appState);
        localStorage.setItem(STORAGE_KEY_PREFIX + sessionId, JSON.stringify(toSave));
    } catch {
        // localStorage quota exceeded — fail silently
    }
}

const LIGHT_CANVAS_BG = "#282a36";
/* Excalidraw dark theme uses invert filter - pass white so canvas inverts to black */
const DARK_CANVAS_BG = "#ffffff";
/* Light mode: white stroke, light gray fill on dracula canvas */
const LIGHT_STROKE = "#ffffff";
const LIGHT_FILL = "#d1d5db";
/* Dark mode: pass dark colors so they invert to light (black→white, dark gray→light gray) */
const DARK_STROKE = "#000000";
const DARK_FILL = "#1a1a1a";

function getDiagramPath(sessionId: string, isPrivate: boolean, userId: string | null): string {
    if (isPrivate && userId) {
        return `private-sandboxes/${userId}/${sessionId}/diagram`;
    }
    return `code-sessions/${sessionId}/diagram`;
}

const POINTER_THROTTLE_MS = 50;

const DiagramView: React.FC<DiagramViewProps> = ({
    darkMode,
    sessionId,
    isPrivate = false,
    socket,
    members = [],
    yourID,
    isCollaborating: isCollaboratingProp,
    onCollaborationChange,
    systemDesignInstructions
}) => {
    const excalidrawApiRef = useRef<ExcalidrawAPI>(null);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isApplyingRemoteRef = useRef(false);
    const lastSavedVersionRef = useRef<number>(0);
    const dbRef = useRef(getDatabase());
    const auth = getAuth();
    const [isCollaboratingLocal, setIsCollaboratingLocal] = useState(false);
    const [instructionsExpanded, setInstructionsExpanded] = useState(true);
    const isCollaborating = isCollaboratingProp ?? isCollaboratingLocal;
    const setCollaborating = (on: boolean) => {
        if (onCollaborationChange) onCollaborationChange(on);
        else setIsCollaboratingLocal(on);
    };
    const containerRef = useRef<HTMLDivElement>(null);
    const appStateRef = useRef<{
        zoom: { value: number };
        offsetLeft: number;
        offsetTop: number;
        scrollX: number;
        scrollY: number;
    } | null>(null);
    const [, setViewportVersion] = useState(0);
    const [remotePointers, setRemotePointers] = useState<Map<string, { sceneX: number; sceneY: number; color: string; name: string }>>(new Map());
    const pointerThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastPointerRef = useRef<{ sceneX: number; sceneY: number } | null>(null);

    const viewBackgroundColor = darkMode ? DARK_CANVAS_BG : LIGHT_CANVAS_BG;
    const defaultStrokeColor = darkMode ? DARK_STROKE : LIGHT_STROKE;
    const defaultFillColor = darkMode ? DARK_FILL : LIGHT_FILL;

    const applyDiagramData = useCallback(
        (api: ExcalidrawAPI, saved: Record<string, unknown> | null) => {
            const savedAppState = saved?.appState as Record<string, unknown> | undefined;
            const isTransparentFill = (c: unknown) =>
                c == null || c === "transparent" || c === "";
            const rawElements = saved?.elements;
            let elements = Array.isArray(rawElements)
                ? rawElements.filter((el) => el != null)
                : rawElements && typeof rawElements === "object"
                  ? Object.values(rawElements).filter((el) => el != null)
                  : [];
            elements = elements.map((el) => {
                if (!el || typeof el !== "object") return el;
                const copy = { ...el };
                if (!Array.isArray(copy.boundElements)) {
                    copy.boundElements = copy.boundElements && typeof copy.boundElements === "object"
                        ? Object.values(copy.boundElements).filter((x) => x != null)
                        : null;
                }
                if (!Array.isArray(copy.groupIds)) {
                    copy.groupIds = copy.groupIds && typeof copy.groupIds === "object"
                        ? Object.values(copy.groupIds).filter((x) => x != null)
                        : [];
                }
                return copy;
            });
            if (saved && elements.length > 0) {
                const appState = {
                    ...(saved.appState as Record<string, unknown>),
                    viewBackgroundColor,
                    currentItemStrokeColor: savedAppState?.currentItemStrokeColor ?? defaultStrokeColor,
                    currentItemBackgroundColor: isTransparentFill(savedAppState?.currentItemBackgroundColor)
                        ? defaultFillColor
                        : savedAppState?.currentItemBackgroundColor,
                };
                api.updateScene({
                    elements: elements as readonly unknown[],
                    appState,
                });
            } else {
                api.updateScene({
                    appState: {
                        viewBackgroundColor,
                        currentItemStrokeColor: defaultStrokeColor,
                        currentItemBackgroundColor: defaultFillColor,
                    },
                });
            }
            requestAnimationFrame(() => api.refresh?.());
        },
        [viewBackgroundColor, defaultStrokeColor, defaultFillColor]
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = useCallback(
        (elements: readonly any[], appState: any) => {
            if (appState && appState.scrollX != null && appState.scrollY != null) {
                const next = {
                    zoom: appState.zoom ?? { value: 1 },
                    offsetLeft: appState.offsetLeft ?? 0,
                    offsetTop: appState.offsetTop ?? 0,
                    scrollX: appState.scrollX,
                    scrollY: appState.scrollY
                };
                const prev = appStateRef.current;
                const viewportChanged =
                    !prev ||
                    prev.scrollX !== next.scrollX ||
                    prev.scrollY !== next.scrollY ||
                    prev.zoom?.value !== next.zoom?.value ||
                    prev.offsetLeft !== next.offsetLeft ||
                    prev.offsetTop !== next.offsetTop;
                appStateRef.current = next;
                if (viewportChanged) setViewportVersion((v) => v + 1);
            }
            if (isApplyingRemoteRef.current) return;
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(() => {
                const version = getSceneVersion(elements);
                if (version === lastSavedVersionRef.current) return;
                saveToStorage(sessionId, elements, appState);
                if (sessionId && dbRef.current) {
                    const user = auth.currentUser;
                    const path = getDiagramPath(sessionId, isPrivate, user?.uid ?? null);
                    const db = dbRef.current;
                    const data = stripUndefined(toDiagramData(elements, appState));
                    update(ref(db, path), data)
                        .then(() => {
                            lastSavedVersionRef.current = version;
                        })
                        .catch(() => {});
                } else {
                    lastSavedVersionRef.current = version;
                }
            }, 400);
        },
        [sessionId, isPrivate]
    );

    const handleApiRef = useCallback(
        (api: ExcalidrawAPI) => {
            excalidrawApiRef.current = api;
            api.updateScene({ appState: { collaborators: new Map() } });
            if (!sessionId) {
                applyDiagramData(api, null);
                return;
            }
            const loadAndApply = (saved: Record<string, unknown> | null) => {
                applyDiagramData(api, saved);
            };
            if (dbRef.current) {
                const user = auth.currentUser;
                const path = getDiagramPath(sessionId, isPrivate, user?.uid ?? null);
                get(ref(dbRef.current, path))
                    .then((snapshot) => {
                        if (snapshot.exists()) {
                            loadAndApply(snapshot.val());
                        } else {
                            loadAndApply(loadFromStorage(sessionId));
                        }
                    })
                    .catch(() => loadAndApply(loadFromStorage(sessionId)));
            } else {
                loadAndApply(loadFromStorage(sessionId));
            }
        },
        [sessionId, isPrivate, applyDiagramData]
    );

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const api = excalidrawApiRef.current;
        if (api) {
            api.updateScene({
                appState: {
                    viewBackgroundColor,
                    currentItemStrokeColor: defaultStrokeColor,
                    currentItemBackgroundColor: defaultFillColor,
                },
            });
        }
    }, [viewBackgroundColor, defaultStrokeColor, defaultFillColor]);

    useEffect(() => {
        if (!sessionId || !dbRef.current) return;
        const user = auth.currentUser;
        const path = getDiagramPath(sessionId, isPrivate, user?.uid ?? null);
        const diagramRef = ref(dbRef.current, path);
        const unsubscribe = onValue(diagramRef, (snapshot) => {
            if (!snapshot.exists()) return;
            const data = snapshot.val();
            const api = excalidrawApiRef.current;
            if (!api) return;
            const remoteElements = Array.isArray(data?.elements)
                ? data.elements
                : data?.elements && typeof data.elements === "object"
                  ? Object.values(data.elements).filter((el: unknown) => el != null)
                  : [];
            if (remoteElements.length > 0) {
                lastSavedVersionRef.current = getSceneVersion(remoteElements as Parameters<typeof getSceneVersion>[0]);
            }
            isApplyingRemoteRef.current = true;
            applyDiagramData(api, data);
            setTimeout(() => {
                isApplyingRemoteRef.current = false;
            }, 0);
        });
        return () => unsubscribe();
    }, [sessionId, isPrivate, applyDiagramData]);

    // Diagram pointer: emit scene coordinates when collaborating (so cursors stay correct after pan/zoom)
    useEffect(() => {
        if (!isCollaborating || !socket?.connected || !sessionId || !containerRef.current) return;

        const container = containerRef.current;
        const handleMouseMove = (e: MouseEvent) => {
            if (pointerThrottleRef.current) return;
            pointerThrottleRef.current = setTimeout(() => {
                pointerThrottleRef.current = null;
                const appState = appStateRef.current;
                let sceneX: number;
                let sceneY: number;
                if (appState) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const scene = viewportCoordsToSceneCoords(
                        { clientX: e.clientX, clientY: e.clientY },
                        appState as any
                    );
                    sceneX = scene.x;
                    sceneY = scene.y;
                } else {
                    const rect = container.getBoundingClientRect();
                    sceneX = e.clientX - rect.left;
                    sceneY = e.clientY - rect.top;
                }
                if (lastPointerRef.current && lastPointerRef.current.sceneX === sceneX && lastPointerRef.current.sceneY === sceneY) return;
                lastPointerRef.current = { sceneX, sceneY };
                socket.emit("diagram-pointer-update", { sessionid: sessionId, x: sceneX, y: sceneY });
            }, POINTER_THROTTLE_MS);
        };

        const handleMouseLeave = () => {
            if (pointerThrottleRef.current) {
                clearTimeout(pointerThrottleRef.current);
                pointerThrottleRef.current = null;
            }
            lastPointerRef.current = null;
            socket.emit("diagram-pointer-leave", { sessionid: sessionId });
        };

        container.addEventListener("mousemove", handleMouseMove);
        container.addEventListener("mouseleave", handleMouseLeave);
        return () => {
            container.removeEventListener("mousemove", handleMouseMove);
            container.removeEventListener("mouseleave", handleMouseLeave);
            if (pointerThrottleRef.current) clearTimeout(pointerThrottleRef.current);
        };
    }, [isCollaborating, socket, sessionId]);

    // Listen for remote diagram pointers
    useEffect(() => {
        if (!socket || !sessionId) return;

        const onPointer = (data: { socketId: string; x: number; y: number; color: string; name: string }) => {
            if (data.socketId === yourID) return;
            setRemotePointers((prev) => {
                const next = new Map(prev);
                next.set(data.socketId, { sceneX: data.x, sceneY: data.y, color: data.color, name: data.name });
                return next;
            });
        };

        const onPointerLeave = (data: { socketId: string }) => {
            setRemotePointers((prev) => {
                const next = new Map(prev);
                next.delete(data.socketId);
                return next;
            });
        };

        socket.on("remote-diagram-pointer", onPointer);
        socket.on("remote-diagram-pointer-leave", onPointerLeave);
        return () => {
            socket.off("remote-diagram-pointer", onPointer);
            socket.off("remote-diagram-pointer-leave", onPointerLeave);
        };
    }, [socket, sessionId, yourID]);

    const hasInstructions = systemDesignInstructions && systemDesignInstructions.trim().length > 0;

    return (
        <div className="diagram-view-container" ref={containerRef}>
            {hasInstructions && (
                <div className={`diagram-instructions-panel ${darkMode ? "dark" : "light"}`}>
                    <button
                        type="button"
                        className="diagram-instructions-toggle"
                        onClick={() => setInstructionsExpanded((e) => !e)}
                        aria-expanded={instructionsExpanded}
                    >
                        <span className="diagram-instructions-title">Interview instructions</span>
                        <span className="diagram-instructions-chevron">{instructionsExpanded ? "▼" : "▶"}</span>
                    </button>
                    {instructionsExpanded && (
                        <div className="diagram-instructions-content">
                            <pre className="diagram-instructions-text">{systemDesignInstructions}</pre>
                        </div>
                    )}
                </div>
            )}
            <Excalidraw
                excalidrawAPI={handleApiRef}
                theme={darkMode ? "dark" : "light"}
                autoFocus
                initialData={{
                    elements: [],
                    appState: {
                        viewBackgroundColor,
                        currentItemStrokeColor: defaultStrokeColor,
                        currentItemBackgroundColor: defaultFillColor,
                    },
                }}
                onChange={handleChange}
                UIOptions={{
                    canvasActions: {
                        saveToActiveFile: false,
                        loadScene: true,
                        export: { saveFileToDisk: true },
                    },
                }}
            />
            {isCollaborating && remotePointers.size > 0 && (() => {
                const appState = appStateRef.current;
                const containerRect = containerRef.current?.getBoundingClientRect();
                if (!containerRect) return null;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const viewportParams = (appState ?? {
                    zoom: { value: 1 },
                    offsetLeft: 0,
                    offsetTop: 0,
                    scrollX: 0,
                    scrollY: 0
                }) as any;
                return (
                    <div className="diagram-pointer-overlay">
                        {Array.from(remotePointers.entries()).map(([socketId, { sceneX, sceneY, color, name }]) => {
                            const viewport = sceneCoordsToViewportCoords(
                                { sceneX, sceneY },
                                viewportParams
                            );
                            const left = viewport.x - containerRect.left;
                            const top = viewport.y - containerRect.top;
                            return (
                                <div
                                    key={socketId}
                                    className="diagram-remote-pointer"
                                    style={{
                                        left,
                                        top,
                                        borderColor: color,
                                        backgroundColor: color
                                    }}
                                    title={name}
                                >
                                    <span className="diagram-remote-pointer-label" style={{ backgroundColor: color }}>
                                        {name}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
        </div>
    );
};

export default DiagramView;
